import { ApiDataResponse } from '@app/types/dto';
import {
  InitSC002Payload,
  InitSC003Payload,
  SC002InitResponse,
  SC003InitResponse,
  SC007CheckDocumentNameExistsPayload,
  SC007CheckDocumentNameExistsResponse,
  SC007DatasetsParams,
  SC007DatasetsResponse,
  SC007DeleteDocumentPayload,
  SC007DeleteDocumentResponse,
  SC007UpdateDocumentPayload,
  SC007UpdateDocumentResponse,
  SC007UploadFromLocalResponse,
} from '@app/types/initScreen';
import {
  AutomationCheckDocsResponse,
  BoxAccessTokenPayload,
  BoxAccessTokenResponse,
  ExportAutomationCheckResultPayload,
  ExportAutomationCheckResultResponse,
  InitSC007Payload,
  SC007InitListResponse,
  SC007UploadImportDocumentPayload,
} from '@app/types/sc007';
import { buildImportDocumentFormData } from '@app/utils/importDocumentFormData';
import { Endpoints } from '@settings/endpoints-setting';
import { apiCall } from './api';
import {
  DOCUMENT_ENDPOINTS,
  PROJECT_ENDPOINTS,
  PROPOSAL_SHARE_STATUS_ENDPOINTS,
  PUBLICATION_ENDPOINTS,
} from './endpointUrl';

const API_ENDPOINT = Endpoints.COMMON.API_ENDPOINT;
/**
 * Screen Initialization API Service
 */
export const screenInitApiService = {
  /**
   * Get SC002 screen initialization data
   * POST /api/publication/init
   */
  getInitSC002: (payload: InitSC002Payload): Promise<ApiDataResponse<SC002InitResponse>> => {
    return apiCall<ApiDataResponse<SC002InitResponse>>(PUBLICATION_ENDPOINTS.INIT_SC002, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * API-SC007-01: Initialize the document management screen.
   * POST /api/document/manage/init
   */
  getInitSC007: (payload: InitSC007Payload): Promise<ApiDataResponse<SC007InitListResponse>> => {
    return apiCall<ApiDataResponse<SC007InitListResponse>>(DOCUMENT_ENDPOINTS.INIT_SC007, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * Get download URL for a document
   * POST /api/document/:documentId/download
   * Note: The API body is { code, messageJp, messageEn, params, data: url }.
   * After response normalization, only the nested `data` (URL string) is returned.
   */
  downloadFile: (documentId: string): Promise<ApiDataResponse<{ content: string }>> => {
    return apiCall<ApiDataResponse<{ content: string }>>(DOCUMENT_ENDPOINTS.DOWNLOAD_FILE(documentId), {
      method: 'POST',
    });
  },

  /**
   * API-SC007-09: Get a list of datasets.
   * POST /api/document/manage/datasets
   */
  getDatasets: (params: SC007DatasetsParams): Promise<ApiDataResponse<SC007DatasetsResponse>> => {
    return apiCall<ApiDataResponse<SC007DatasetsResponse>>(PROJECT_ENDPOINTS.DATASETS, {
      method: 'POST',
      data: params,
    });
  },

  /**
   * Get SC003 screen initialization data
   * POST /api/proposal-share-status/init
   */
  getInitSC003: (payload: InitSC003Payload): Promise<ApiDataResponse<SC003InitResponse>> => {
    return apiCall<ApiDataResponse<SC003InitResponse>>(PROPOSAL_SHARE_STATUS_ENDPOINTS.INIT_SC003, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * API-SC007-04: Update document metadata
   * PUT /api/document/manage/document-metadata
   */
  updateDocumentMetadata: (payload: SC007UpdateDocumentPayload): Promise<SC007UpdateDocumentResponse> => {
    return apiCall<SC007UpdateDocumentResponse>(DOCUMENT_ENDPOINTS.UPDATE_DOCUMENT_METADATA, {
      method: 'PUT',
      data: payload,
    });
  },

  /**
   * API-SC007-05: Delete a document (logic delete)
   * DELETE /api/document/manage/delete-document
   * [DD-SC007-D002] § 4.2.2
   */
  deleteDocument: (payload: SC007DeleteDocumentPayload): Promise<SC007DeleteDocumentResponse> => {
    return apiCall<SC007DeleteDocumentResponse>(DOCUMENT_ENDPOINTS.DELETE_DOCUMENT, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * Get automation check result for a document
   * GET /api/document/:documentId/automation-check-result
   * [DD-SC007-D004] § 4.1.2 — API-SC007-07
   */
  getDocumentAutomationCheckResult: (documentId: number): Promise<ApiDataResponse<AutomationCheckDocsResponse>> => {
    return apiCall<ApiDataResponse<AutomationCheckDocsResponse>>(
      DOCUMENT_ENDPOINTS.AUTOMATION_CHECK_RESULT_BY_ID(documentId),
      { method: 'GET' }
    );
  },

  /**
   * Export automation check result for a document
   * POST /api/document/manage/{documentId}/export-automation-check-result
   */
  exportAutomationCheckResult: (
    documentId: number,
    payload: ExportAutomationCheckResultPayload
  ): Promise<ApiDataResponse<ExportAutomationCheckResultResponse>> => {
    return apiCall<ApiDataResponse<ExportAutomationCheckResultResponse>>(
      DOCUMENT_ENDPOINTS.EXPORT_AUTOMATION_CHECK_RESULT(documentId),
      {
        method: 'POST',
        data: payload,
      }
    );
  },

  /**
   * API-SC007-11: Obtain an access token for the Box Content Picker.
   * POST /api/document/manage/box-access-token
   * [DD-SC007] § 4.2.2 — btnSelectBox
   */
  getBoxAccessToken: (payload: BoxAccessTokenPayload): Promise<ApiDataResponse<BoxAccessTokenResponse>> => {
    return apiCall<ApiDataResponse<BoxAccessTokenResponse>>(DOCUMENT_ENDPOINTS.BOX_ACCESS_TOKEN, {
      method: 'POST',
      data: payload,
    });
  },

  /**
   * API-SC007-08: Import document
   * POST /api/document/import-document
   * FormData is built by buildImportDocumentFormData() — maps 1:1 with the cURL spec:
   *   -F 'importDocumentRequests={...};type=application/json'
   *   -F 'files[]=@<name>;type=<mime>'
   */
  importDocument: (
    payload: SC007UploadImportDocumentPayload
  ): Promise<ApiDataResponse<SC007UploadFromLocalResponse>> => {
    return apiCall<ApiDataResponse<SC007UploadFromLocalResponse>>(DOCUMENT_ENDPOINTS.IMPORT_DOCUMENTS, {
      method: 'POST',
      data: buildImportDocumentFormData(payload),
    });
  },

  /**
   * API-SC007-10: Check if document names already exist in a project
   * POST /api/document/manage/check-document-name-exists
   */
  checkDocumentNameExists: (
    payload: SC007CheckDocumentNameExistsPayload
  ): Promise<ApiDataResponse<SC007CheckDocumentNameExistsResponse>> => {
    return apiCall<ApiDataResponse<SC007CheckDocumentNameExistsResponse>>(
      PROJECT_ENDPOINTS.CHECK_DOCUMENT_NAME_EXISTS,
      { method: 'POST', data: payload }
    );
  },
};
